import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Chip,
  Divider,
  alpha,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon, ExpandLess, ExpandMore } from '@mui/icons-material';
import { MENU_ITEMS } from '../constants/layoutConstants';
import usePermissions from '../../hooks/usePermissions';
import logo from '../../assets/img/logo_constructora.png';
import { useAuth } from '../../hooks/useAuth';

const MobileNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [openItems, setOpenItems] = useState({});
  const { hasModule, hasAnyModule, hasAllModules } = usePermissions();
  const { clearToken } = useAuth();

  const filterMenuItems = (items) =>
    items.reduce((filtered, item) => {
      if (item.isVersionTag) {
        filtered.push(item);
        return filtered;
      }

      const hasPath = Boolean(item.path);
      const filteredChildren = item.children ? filterMenuItems(item.children) : [];
      const hasVisibleChildren = filteredChildren.length > 0;
      const canAccessItem =
        (!item.requiredModule || hasModule(item.requiredModule)) &&
        (!item.requiredAnyModules || hasAnyModule(item.requiredAnyModules)) &&
        (!item.requiredAllModules || hasAllModules(item.requiredAllModules));

      if (item.children && !hasPath) {
        if (hasVisibleChildren) {
          filtered.push({
            ...item,
            children: filteredChildren,
          });
        }
        return filtered;
      }

      if (!canAccessItem) return filtered;

      if (item.children) {
        if (!hasVisibleChildren) return filtered;

        filtered.push({
          ...item,
          children: filteredChildren,
        });
        return filtered;
      }

      filtered.push(item);
      return filtered;
    }, []);

  const filteredMenuItems = filterMenuItems(MENU_ITEMS);

  const isItemActive = (path) => {
    return location.pathname === path;
  };

  const isParentActive = (item) => {
    if (item.path && isItemActive(item.path)) return true;
    if (item.children) {
      return item.children.some((child) => {
        if (child.path && isItemActive(child.path)) return true;
        if (child.children) {
          return child.children.some(
            (grandchild) => grandchild.path && isItemActive(grandchild.path)
          );
        }
        return false;
      });
    }
    return false;
  };

  const handleToggleDrawer = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setOpenItems({});
    }
  };

  const handleItemAction = (item) => {
    if (item?.isLogout) {
      clearToken();
      navigate('/');
      setIsOpen(false);
      setOpenItems({});
      return;
    }

    if (item?.path) {
      navigate(item.path);
      setIsOpen(false);
      setOpenItems({});
    }
  };

  const handleSubmenuToggle = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderMenuItem = (item, index, level = 0, parentKey = '') => {
    const hasChildren = item.children && item.children.length > 0;
    const itemKey = parentKey ? `${parentKey}-${index}` : `${level}-${index}`;
    const isOpen = openItems[itemKey];
    const isActive = isParentActive(item);

    if (item.isVersionTag) {
      return (
        <Box key={itemKey} sx={{ p: 2, textAlign: 'center' }}>
          <Chip
            label={item.label}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 'bold',
            }}
          />
        </Box>
      );
    }

    const isLogout = Boolean(item.isLogout);

    return (
      <Box key={itemKey}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                handleSubmenuToggle(itemKey);
              } else {
                handleItemAction(item);
              }
            }}
            sx={{
              pl: 2 + level * 2,
              py: 1.5,
              minHeight: 48,
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              bgcolor: isActive ? (theme) => alpha(theme.palette.primary.main, 0.1) : 'transparent',
              color: isLogout ? 'error.main' : isActive ? 'primary.main' : 'text.primary',
              '&:hover': isLogout
                ? {
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                    color: 'error.main',
                  }
                : {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                  },
            }}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: isLogout ? 'error.main' : isActive ? 'primary.main' : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
            )}

            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: level === 0 ? '1rem' : '0.875rem',
                fontWeight: level === 0 ? 600 : 500,
                color: isLogout ? 'error.main' : isActive ? 'primary.main' : 'inherit',
              }}
            />

            {hasChildren && (
              <Box sx={{ color: 'text.secondary' }}>{isOpen ? <ExpandLess /> : <ExpandMore />}</Box>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children.map((child, childIndex) =>
                renderMenuItem(child, childIndex, level + 1, itemKey)
              )}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <AppBar
          position="static"
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 'none',
            borderBottom: '1px solid',
            borderColor: 'divider',
            width: '100%',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box
              component="a"
              href="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <img
                src={logo}
                alt="Logo Constructora"
                style={{
                  height: '36px',
                  objectFit: 'contain',
                }}
              />
            </Box>

            <IconButton
              edge="end"
              color="primary"
              onClick={handleToggleDrawer}
              sx={{
                p: 1,
                bgcolor: isOpen ? 'action.selected' : 'transparent',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {isOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>
      </Box>

      <Drawer
        anchor="top"
        open={isOpen}
        onClose={handleToggleDrawer}
        PaperProps={{
          sx: {
            mt: 7,
            maxHeight: 'calc(100vh - 64px)',
            bgcolor: 'background.paper',
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'background.default',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'action.disabled',
              borderRadius: '3px',
            },
          },
        }}
      >
        <Box sx={{ py: 2 }}>
          <Typography
            variant="overline"
            sx={{
              px: 3,
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            Menú Principal
          </Typography>
          <Divider sx={{ mt: 1, mb: 2 }} />

          <List disablePadding>
            {filteredMenuItems.map((item, index) => renderMenuItem(item, index))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileNavigation;
